import { CircleXIcon, FileTextIcon, NotebookPenIcon } from 'lucide-react'
import { motion } from 'motion/react'
import { FC, HTMLAttributes, PropsWithChildren, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Button, buttonVariants } from '@/components/ui/button'
import { cn } from '@/lib/utils'

import MarkdownRenderer from '../MarkdownRenderer'

const Base: FC<PropsWithChildren<HTMLAttributes<HTMLDivElement>>> = ({ children, ...props }) => {
  return (
    <div className={cn(`mb-8 w-fit rounded-xl px-5 py-3 text-wrap`, props.className)}>
      {children}
    </div>
  )
}

type SourcePill = {
  id: string
  icon: ReactNode
  label: string
  /** Absent when the source is no longer reachable — the pill renders, but inert. */
  onOpen?(): void
}

type AiProps = {
  /**
   * Set on a message the assistant wrote on its own initiative, not in reply to the user — the
   * already-translated text to show in the marker above it (e.g. "Dokument uppladdade" or
   * "Status ändrad från Påbörjad till Inskickad").
   */
  automatedLabel?: string
  children?: string
  onOpenNote?(noteId: string): void
  onPreviewDocument?(documentId: string): void
  /**
   * Ids the caller can actually open. A pill for a source since removed from the errand still
   * renders — the message is a record of what was read at the time — but isn't clickable.
   */
  openableNoteIds?: Set<string>
  previewableDocumentIds?: Set<string>
  /** Documents an automated message was written about — shown as pills under the marker. */
  sourceDocuments?: { id: string; name: string }[]
  /** Notes the same message was written about. Clicking one opens the note itself. */
  sourceNotes?: { id: string; title: string }[]
}

const ChatMessageAi: FC<AiProps> = ({
  automatedLabel,
  children,
  onOpenNote,
  onPreviewDocument,
  openableNoteIds,
  previewableDocumentIds,
  sourceDocuments,
  sourceNotes,
}) => {
  const { t } = useTranslation()

  // Documents and notes are one row of pills, not two: they are the same thing to the reader —
  // what the assistant was looking at when it wrote this — and an upload and a note can land in
  // the same reaction. Documents lead because that is the order the label names them in.
  //
  // Deliberately sources only. A status change gets no pill: the marker above already spells out
  // "Status ändrad från X till Y", so a pill next to it would only say the same thing twice.
  const pills: SourcePill[] = [
    ...(sourceDocuments ?? []).map(document => ({
      icon: <FileTextIcon className="shrink-0" size={14} />,
      id: `document-${document.id}`,
      label: document.name,
      onOpen:
        onPreviewDocument && previewableDocumentIds?.has(document.id)
          ? () => onPreviewDocument(document.id)
          : undefined,
    })),
    ...(sourceNotes ?? []).map(note => ({
      icon: <NotebookPenIcon className="shrink-0" size={14} />,
      id: `note-${note.id}`,
      // A note reacted to before it had an AI title (or one the user cleared) still needs
      // something on its pill.
      label: note.title || t('notesTable.untitled'),
      onOpen: onOpenNote && openableNoteIds?.has(note.id) ? () => onOpenNote(note.id) : undefined,
    })),
  ]

  return (
    <>
      {automatedLabel && (
        <div className="mb-2 flex w-fit items-center rounded-full bg-brand-funkia px-3 py-1.5 text-white">
          <p className="text-sm font-medium">{automatedLabel}</p>
        </div>
      )}
      {/* What the assistant was actually looking at when it wrote this — without them a reader
          can't tell whether it covered the file they just uploaded or an older one. Same
          secondary-button treatment as a document pill on the errand's event list. */}
      {pills.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-1.5">
          {pills.map(pill =>
            pill.onOpen ? (
              <Button
                className="gap-1.5"
                key={pill.id}
                onClick={pill.onOpen}
                size="sm"
                title={pill.label}
                type="button"
                variant="secondary"
              >
                {pill.icon}
                <span className="max-w-[18ch] truncate">{pill.label}</span>
              </Button>
            ) : (
              <span
                className={cn(
                  buttonVariants({ size: 'sm', variant: 'secondary' }),
                  'cursor-default gap-1.5',
                )}
                key={pill.id}
                title={pill.label}
              >
                {pill.icon}
                <span className="max-w-[18ch] truncate">{pill.label}</span>
              </span>
            ),
          )}
        </div>
      )}
      <Base className="p-0">
        {children ? <MarkdownRenderer className="max-w-prose">{children}</MarkdownRenderer> : null}
      </Base>
    </>
  )
}

type UserProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  attachments?: any[]
}

const ChatMessageUser: FC<PropsWithChildren<UserProps>> = ({ attachments, children }) => {
  return (
    <Base className="bg-layer-2 border ml-auto max-w-[80%]">
      {typeof children === 'string' ? <MarkdownRenderer>{children}</MarkdownRenderer> : children}
      {(attachments?.length ?? 0) > 0 && (
        <div className="space-y-2">
          {attachments?.map(attachment => (
            <div key={attachment.name}>
              <a
                className="underline"
                download={attachment.name}
                href={attachment.url}
                rel="noopener noreferrer"
                target="_blank"
              >
                {attachment.name}
              </a>
            </div>
          ))}
        </div>
      )}
    </Base>
  )
}

const ChatMessageLoading = ({ thinkingMessage }: { thinkingMessage?: string }) => {
  const { t } = useTranslation()

  return (
    <Base className="p-0">
      <motion.p
        animate={{ opacity: 1, translateX: 0 }}
        className="animate-pulse italic"
        initial={{ opacity: 0, translateX: 20 }}
        transition={{ duration: 0.3 }}
      >
        {thinkingMessage ?? t('aiChat.thinking')}
      </motion.p>
    </Base>
  )
}

const ChatMessageError = ({ error }: { error: Error | string }) => {
  return (
    <Base className="bg-error-bg border border-error-border text-error">
      <div className="flex items-center gap-2">
        <CircleXIcon size={18} />
        {error instanceof Error ? error.message : error}
      </div>
    </Base>
  )
}

export { ChatMessageAi, ChatMessageError, ChatMessageLoading, ChatMessageUser }
